import { type AnyAction, combineReducers } from 'redux';

import { VideoStatus } from 'js/src/client';
import {
    setLoadingFinished,
    setVideoName,
    setQueuedArtifactDownload,
    setVideoStatus,
    setVideoVisibility,
    startAutoDownload,
    storeRenderEstimate,
    storeVideo,
} from 'js/src/data/share-video/share-video-actions';
import type {
    PublicVideoRenderEstimate,
    ReadonlyPublicVideo,
} from 'js/src/data/share-video/share-video-types';
import type { DeepReadonly } from 'js/src/libs/consts';

const withVideoField = <Field extends 'status' | 'visibility' | 'name'>(
    state: ReadonlyPublicVideo | null,
    field: Field,
    value: ReadonlyPublicVideo[Field],
): ReadonlyPublicVideo | null => {
    if (!state || state[field] === value) {
        return state;
    }
    return { ...state, [field]: value };
};

export function video(
    state: ReadonlyPublicVideo | null = null,
    action: AnyAction,
): ReadonlyPublicVideo | null {
    if (storeVideo.match(action)) {
        return action.payload;
    }
    if (setVideoStatus.match(action)) {
        return withVideoField(state, 'status', action.payload);
    }
    if (setVideoVisibility.match(action)) {
        return withVideoField(state, 'visibility', action.payload);
    }
    if (setVideoName.match(action)) {
        return withVideoField(state, 'name', action.payload);
    }
    return state;
}

export function renderEstimate(
    state: DeepReadonly<PublicVideoRenderEstimate> | null = null,
    action: AnyAction,
): DeepReadonly<PublicVideoRenderEstimate> | null {
    if (storeRenderEstimate.match(action)) {
        return action.payload;
    }
    return state;
}

export function loading(state = true, action: AnyAction) {
    if (setLoadingFinished.match(action)) {
        return false;
    }
    return state;
}

export function autoDownload(state = false, action: AnyAction) {
    if (startAutoDownload.match(action)) {
        return true;
    }
    return state;
}

export function queuedArtifactDownload(state: string | null = null, action: AnyAction) {
    if (setQueuedArtifactDownload.match(action)) {
        return action.payload;
    }
    return state;
}

/**
 * Whether the stored video carries render output (the per-asset `renderUrl`s) and not just a
 * PUBLISHED status. `setVideoStatus` flips the status a network round-trip before the refetched
 * video lands, so anything reading rendered media has to wait for the `storeVideo` that follows.
 */
export function renderDataLoaded(state = false, action: AnyAction) {
    if (storeVideo.match(action)) {
        return action.payload.status === VideoStatus.PUBLISHED;
    }
    if (setVideoStatus.match(action)) {
        return state && action.payload === VideoStatus.PUBLISHED;
    }
    return state;
}

export const shareVideo = combineReducers({
    video,
    renderEstimate,
    loading,
    autoDownload,
    queuedArtifactDownload,
    renderDataLoaded,
});

export type ShareVideoState = ReturnType<typeof shareVideo>;
